import { styled } from "@linaria/react"
import { useMatches, useNavigate, useParams } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"

import { Avatar } from "@/components/Avatar/Avatar.tsx"
import { Button } from "@/components/Button/Button.tsx"
import { Centered } from "@/components/Centered/Centered.ts"
import { ConfirmDialog, Dialog } from "@/components/Dialog/Dialog.tsx"
import { Ellipsis } from "@/components/Ellipsis/Ellipsis.ts"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { Heading2 } from "@/components/Heading/Heading.tsx"
import { SettingsDialogStyles } from "@/components/ProjectSettingsDialog/SettingsDialogStyles.ts"
import { Scrollable } from "@/components/Scrollable/Scrollable.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { getFullName, useMyPrincipal, useMyProfile } from "@/hooks/useProfile.ts"
import { useOrgContext, useQueries } from "@/hooks/useRootContext.ts"
import { FigmaColor, Icon20, SlackColor } from "@/icons/Icons.ts"
import { UserSettingsTeammatesContent } from "@/routes/auth/InviteTeammatesDialog.tsx"
import { SlackNotifications } from "@/routes/auth/SlackNotifications.tsx"
import { Navigator } from "@/routes/Navigator.ts"
import { SshKeysTabContent } from "@/routes/user/SshKeysTabContent.tsx"
import { UserSettingsDialogModel } from "@/routes/user/UserSettingsDialogModel.ts"
import { TabId } from "@/routes/user/UserSettingsTabId.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { TD_MemberProfile } from "@/lib/client/generated/TypeDefinitions.ts"


export const UserSettingsDialog = ({ dialogOpen, setDialogOpen }: {
  dialogOpen: boolean
  setDialogOpen: (open: boolean) => void
}) => {

  return (
    <Dialog
      top="128px"
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      trigger={null}
      title={<DialogTitle>Settings</DialogTitle>}
      maxWidth="868px"
    >
      <SettingsDialogContent setDialogOpen={setDialogOpen} />
    </Dialog>
  )
}

const SettingsDialogContent = observer(({ setDialogOpen }: {
  setDialogOpen: (open: boolean) => void
}) => {
  const navigate = useNavigate()
  const initialTab = useParams({ strict: false }).tab
  const isSettingsRoute = useMatches().some(it => it.routeId === "/$orgId/settings/$tab")

  const context = useOrgContext()
  const orgId = context.orgId

  const queries = useQueries()
  const profile = useMyProfile()
  const principal = useMyPrincipal()

  const model = useModel(() => new UserSettingsDialogModel(initialTab, {
    context, queries, profile, principal
  }), [initialTab, context, queries, profile, principal])

  const currentTab = model.currentTab.get()

  useEffect(() => {
    if (isSettingsRoute) {
      void navigate(Navigator.settings(orgId, currentTab))
    }
  }, [isSettingsRoute, navigate, orgId, currentTab])

  return (
    <SettingsDialogStyles.Layout>
      <SettingsDialogStyles.Sidebar>
        <Scrollable childrenContainerClassName={SettingsDialogStyles.SidebarInner}>
          <SettingsDialogStyles.SidebarTitle>Account</SettingsDialogStyles.SidebarTitle>

          <SettingsDialogStyles.SidebarItem
            active={currentTab == TabId.Profile}
            data-testid="settings-sideitem-profile"
          >
            <SettingsDialogStyles.SidebarLink
              {...Navigator.settings(orgId, TabId.Profile)}
              onClick={model.onTabClick(TabId.Profile)}
            >
              Profile
            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>

          <SettingsDialogStyles.SidebarItem
            active={currentTab == TabId.SshKeys}
            data-testid="settings-sideitem-ssh-keys"
          >
            <SettingsDialogStyles.SidebarLink
              {...Navigator.settings(orgId, TabId.SshKeys)}
              onClick={model.onTabClick(TabId.SshKeys)}
            >
              SSH Keys
            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>

          <SettingsDialogStyles.SidebarSeparator />

          <SettingsDialogStyles.SidebarTitle>Workspace</SettingsDialogStyles.SidebarTitle>

          <SettingsDialogStyles.SidebarItem
            active={currentTab == TabId.Teammates}
            data-testid="settings-sideitem-teammates"
          >
            <SettingsDialogStyles.SidebarLink
              {...Navigator.settings(orgId, TabId.Teammates)}
              onClick={model.onTabClick(TabId.Teammates)}
            >
              Teammates
            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>

          <SettingsDialogStyles.SidebarSeparator />

          <SettingsDialogStyles.SidebarTitle>Integrations</SettingsDialogStyles.SidebarTitle>

          <SettingsDialogStyles.SidebarItem active={currentTab == TabId.Figma} data-testid="settings-sideitem-figma">
            <SettingsDialogStyles.SidebarLink
              {...Navigator.settings(orgId, TabId.Figma)}
              onClick={model.onTabClick(TabId.Figma)}
            >
              <IntegrationName>
                <FigmaColor className={Icon20} />
                Figma
              </IntegrationName>

            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>

          <SettingsDialogStyles.SidebarItem active={currentTab == TabId.Slack} data-testid="settings-sideitem-slack">
            <SettingsDialogStyles.SidebarLink
              {...Navigator.settings(orgId, TabId.Slack)}
              onClick={model.onTabClick(TabId.Slack)}
            >
              <IntegrationName>
                <SlackColor className={Icon20} />
                Slack
              </IntegrationName>
            </SettingsDialogStyles.SidebarLink>
          </SettingsDialogStyles.SidebarItem>
        </Scrollable>
      </SettingsDialogStyles.Sidebar>

      <SettingsDialogStyles.Separator />

      <SettingsDialogStyles.Content>
        <Scrollable childrenContainerClassName={SettingsDialogStyles.ContentInner}>
          {currentTab == TabId.Profile && model.profile && (
            <ProfileTab profile={model.profile} />
          )}

          {currentTab == TabId.SshKeys && model.sshModel && (
            <SshKeysTab model={model} />
          )}

          {currentTab == TabId.Teammates && (
            <TeammatesTab model={model} setDialogOpen={setDialogOpen} />
          )}

          {currentTab == TabId.Figma && (
            <FigmaTab model={model} />
          )}

          {currentTab == TabId.Slack && (
            <SlackTab model={model} />
          )}
        </Scrollable>
      </SettingsDialogStyles.Content>
    </SettingsDialogStyles.Layout>
  )
})

const ProfileTab = ({ profile }: { profile: TD_MemberProfile }) => {
  return (
    <Vertical gap="16px">
      <Heading2>Profile</Heading2>

      <Centered gap="16px">
        <Avatar profile={profile} size={128} />

        <span>{getFullName(profile)}</span>
      </Centered>
    </Vertical>
  )
}

const SshKeysTab = (props: { model: UserSettingsDialogModel }) => {
  return (
    <Vertical gap="16px">
      <Heading2>SSH Keys</Heading2>

      <SshKeysTabContent model={props.model.sshModel!} editable />
    </Vertical>
  )
}

const TeammatesTab = ({ model, setDialogOpen }: {
  model: UserSettingsDialogModel,
  setDialogOpen: (open: boolean) => void
}) => {
  return (
    <Vertical gap="24px" alignItems="flex-start">
      <Heading2>Teammates</Heading2>

      <UserSettingsTeammatesContent model={model.inviteModel} setDialogOpen={setDialogOpen} />
    </Vertical>
  )
}

const FigmaTab = observer((props: {
  model: UserSettingsDialogModel
}) => {
  const figmaModel = props.model.figmaModel

  const [showDisconnectConfirmation, setShowDisconnectConfirmation] = useState(false)
  const handleDisconnectConfirmed = () => {
    void figmaModel.disconnectFromFigma()
    setShowDisconnectConfirmation(false)
  }

  const figmaStatus = figmaModel.figmaStatus.get()

  return (
    <Vertical gap="16px">
      <Heading2>Figma Integration</Heading2>

      <div>Connect your Figma account to access your designs directly in Matter.</div>

      <Vertical>
        {figmaStatus && figmaStatus.isConnected && (
          <>
            <ConnectionRow gap="16px">
              <ConnectionBg>
                <FigmaColor className={Icon20} />
                <FigmaUsername>{figmaStatus.userName}</FigmaUsername>
                <StyledEllipsis>{figmaStatus.email}</StyledEllipsis>
              </ConnectionBg>

              <Button kind="alternative" size="large" onClick={() => setShowDisconnectConfirmation(true)}>
                Disconnect
              </Button>
            </ConnectionRow>

            <ConfirmDialog
              open={showDisconnectConfirmation}
              onOpenChange={setShowDisconnectConfirmation}
              description="Are you sure you want to disconnect your Figma account? You'll need to reconnect to access your designs in Matter."
              confirmText="Disconnect"
              onConfirm={handleDisconnectConfirmed}
              trigger={null}
            />
          </>
        )}

        {figmaStatus && !figmaStatus.isConnected && (
          <Horizontal>
            <Button kind="secondary" size="large" onClick={figmaModel.connectToFigma}>
              Connect to Figma
            </Button>
          </Horizontal>
        )}

        {!figmaStatus && <Spinner text={ellipsize("Loading")} />}
      </Vertical>
    </Vertical>
  )
})


const SlackTab = (props: {
  model: UserSettingsDialogModel
}) => {
  return (
    <Vertical gap="16px">
      <Heading2>Slack integration</Heading2>

      <div>Receive Slack notifications when someone mentions you in Matter.</div>

      <SlackNotifications model={props.model.slackModel} />
    </Vertical>
  )
}

const DialogTitle = styled(SettingsDialogStyles.Title)`
  display: flex;
  align-items: center;
  padding-right: 20px;
`

const IntegrationName = styled(Horizontal)`
  align-items: center;
  gap: 4px;
`

const ConnectionRow = styled(Horizontal)`
  min-width: 0;
  align-items: center;
`

const FigmaUsername = styled.span`
  font-weight: 500;
  flex: 0 0 auto;
  white-space: nowrap;
`

const StyledEllipsis = styled(Ellipsis)`
  flex: 1 1 auto;
  min-width: 0;
  ${textStyles.smallestFontSize};
`

const ConnectionBg = styled(Horizontal)`
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  gap: 8px;

  height: 40px;
  padding: 0 16px;

  background: var(--text-color-2);
  border-radius: var(--border-radius-12);
  overflow: hidden;
`
